import {
  useQuery,
  useQueryClient,
  useMutation,
  UseQueryOptions,
  UseMutationOptions,
  UseMutateFunction,
} from '@tanstack/react-query';
import UserGeneratedContentService from '../../services/user-generated-content';
import {
  UserGeneratedContentResponse,
  UserGeneratedContent,
} from '../../models/user-generated-contents/types';

export const USER_GENERATED_CONTENTS_QUERY_KEY = ['UserGeneratedContent'];
export const USER_GENERATED_CONTENTS_LIST_QUERY_KEY = [
  'UserGeneratedContentsList',
];

type UserGeneratedContentMutationReturnType = {
  isWorking: boolean;
  mutate: UseMutateFunction<
    UserGeneratedContentResponse,
    Error,
    UserGeneratedContent
  >;
};

export type QueryResponse<T> = {
  isLoading: boolean;
  errorMessage?: string;
  error?: Error | null;
  data?: T;
  isSuccess?: boolean;
  invalidateQuery?: () => void;
  isFetching?: boolean | undefined;
};

export const useUserGeneratedContentQuery = (
  id: number | undefined,
  options?: Omit<
    UseQueryOptions<
      UserGeneratedContentResponse,
      Error,
      UserGeneratedContentResponse,
      string[]
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    UserGeneratedContentResponse,
    Error,
    UserGeneratedContentResponse,
    string[]
  >({
    queryKey: [...USER_GENERATED_CONTENTS_QUERY_KEY, String(id)],
    queryFn: async () => {
      if (!id) {
        throw new Error('An ID is required');
      }

      const service = new UserGeneratedContentService(id);
      const response = await service.fetch();

      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};

export const useCreateUserGeneratedContentMutation = (
  options?: Omit<
    UseMutationOptions<
      UserGeneratedContentResponse,
      Error,
      UserGeneratedContent
    >,
    'mutationFn'
  >
): UserGeneratedContentMutationReturnType => {
  const queryClient = useQueryClient();
  const { isLoading, mutate } = useMutation<
    UserGeneratedContentResponse,
    Error,
    UserGeneratedContent
  >({
    mutationFn: async (data: UserGeneratedContent) => {
      const service = new UserGeneratedContentService(undefined);
      const response = await service.create(data);
      return response.data;
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([
        ...USER_GENERATED_CONTENTS_QUERY_KEY,
        String(data.data.attributes.id),
      ]);
      options?.onSuccess?.(data, variables, context);
    },
  });

  return { isWorking: isLoading, mutate };
};

export const useUpdateUserGeneratedContentMutation = (
  id?: number,
  options?: Omit<
    UseMutationOptions<
      UserGeneratedContentResponse,
      Error,
      UserGeneratedContent
    >,
    'mutationFn'
  >
): UserGeneratedContentMutationReturnType => {
  const queryClient = useQueryClient();
  const { isLoading, mutate } = useMutation<
    UserGeneratedContentResponse,
    Error,
    UserGeneratedContent
  >({
    mutationFn: async (data: UserGeneratedContent) => {
      if (!id) throw new Error('An ID is required');

      const service = new UserGeneratedContentService(id);
      const response = await service.update(data);
      return response.data;
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(
        [...USER_GENERATED_CONTENTS_QUERY_KEY, String(id)],
        data
      );
      options?.onSuccess?.(data, variables, context);
    },
  });
  return { isWorking: isLoading, mutate };
};

export const useUpdateUserGeneratedContentStatusMutation = (
  options?: Omit<
    UseMutationOptions<
      UserGeneratedContentResponse,
      Error,
      UserGeneratedContent
    >,
    'mutationFn'
  >
): UserGeneratedContentMutationReturnType => {
  const { isLoading, mutate } = useMutation<
    UserGeneratedContentResponse,
    Error,
    UserGeneratedContent
  >({
    mutationFn: async (data: UserGeneratedContent) => {
      if (!data.id) throw new Error('An ID is required');

      const service = new UserGeneratedContentService(data.id);
      const response = await service.updateStatus(data.status);
      return response.data;
    },
    ...options,
  });
  return { isWorking: isLoading, mutate };
};
